import type { FC } from 'react';

import { useCms } from '@eneco/cms';
import RichText from '@eneco/components/RichText/RichText';
import { Heading, InputEmail, InputText, InputTelephone, RadioGroup, RadioButton, Grid, Text } from '@eneco/sparky';
import { UseFormRegister, Control, Controller, FieldErrors } from 'react-hook-form';

import type { FormValues } from './types';
import { hintOf } from '../contact-form/hint';
import type { DeceasedFormContent } from '../deceased-form-content.types';

type Props = {
  register: UseFormRegister<FormValues>;
  content: DeceasedFormContent;
  control: Control<FormValues, string>;
  errors: FieldErrors<FormValues>;
};

/**
 * Kept as the legacy has it: the salutation offers only "dhr"/"mevr", looking their labels up under the
 * names `mr`/`mrs`, so content naming its options otherwise (`male`/`female`/`unknown`, as both CMSes
 * do today) shows the "Dhr."/"Mevr." fallbacks; and the surname preposition and house number suffix
 * show the surname's and house number's hint and placeholder.
 */
export const ContactInputs: FC<Props> = ({ register, content, errors, control }) => {
  const { adapter } = useCms();
  const {
    contactFormFields_title: title,
    salutationFormField,
    firstNameFormField,
    surnamePrepositionFormField,
    surnameFormField,
    emailAddressFormField,
    contactFormFields_houseNumberFormField: houseNumberFormField,
    contactFormFields_houseNumberSuffixFormField: houseNumberSuffixFormField,
    phoneNumberFormField,
    contactFormFields_postalCodeFormField: postalCodeFormField,
  } = content;
  return (
    <>
      <Heading as="h3" size="S">
        {title}
      </Heading>
      <Text size={{ initial: 'BodyM', md: 'BodyL' }}>
        <RichText inline html={adapter.toRichText(content.contactFormFields_description)} />
      </Text>
      <Controller
        control={control}
        name="contactSalutation"
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            name={name}
            value={value}
            onValueChange={onChange}
            label={salutationFormField?.label ?? ''}
            error={errors?.contactSalutation?.message}
            hint={hintOf(salutationFormField?.hint)}>
            <RadioButton value="dhr">
              {salutationFormField?.options?.find(option => option.name === 'mr')?.label ?? `Dhr.`}
            </RadioButton>
            <RadioButton value="mevr">
              {salutationFormField?.options?.find(option => option.name === 'mrs')?.label ?? `Mevr.`}
            </RadioButton>
          </RadioGroup>
        )}
      />

      <Grid columns={{ initial: '1', md: '2', lg: '3' }} gap="6">
        <InputText
          label={firstNameFormField?.label ?? ''}
          error={errors?.contactFirstName?.message}
          hint={hintOf(firstNameFormField?.hint)}
          placeholder={firstNameFormField?.placeholder ?? ''}
          {...register('contactFirstName')}
        />
        <InputText
          label={surnamePrepositionFormField?.label ?? ''}
          error={errors?.contactSurnamePreposition?.message}
          hint={hintOf(surnameFormField?.hint)}
          placeholder={surnameFormField?.placeholder ?? ''}
          {...register('contactSurnamePreposition')}
          isOptional
        />
        <InputText
          label={surnameFormField?.label ?? ''}
          error={errors?.contactSurname?.message}
          hint={hintOf(surnameFormField?.hint)}
          placeholder={surnameFormField?.placeholder ?? ''}
          {...register('contactSurname')}
        />
      </Grid>
      <Grid columns={{ initial: '1', md: '3' }} gap="6">
        <InputText
          label={postalCodeFormField?.label ?? ''}
          error={errors?.contactPostalCode?.message}
          hint={hintOf(postalCodeFormField?.hint)}
          placeholder={postalCodeFormField?.placeholder ?? ''}
          {...register('contactPostalCode')}
        />
        <InputText
          label={houseNumberFormField?.label ?? ''}
          error={errors?.contactHouseNumber?.message}
          hint={hintOf(houseNumberFormField?.hint)}
          placeholder={houseNumberFormField?.placeholder ?? ''}
          {...register('contactHouseNumber')}
        />
        <InputText
          label={houseNumberSuffixFormField?.label ?? ''}
          error={errors?.contactHouseNumberSuffix?.message}
          hint={hintOf(houseNumberSuffixFormField?.hint)}
          placeholder={houseNumberFormField?.placeholder ?? ''}
          isOptional
          {...register('contactHouseNumberSuffix')}
        />
      </Grid>
      <Grid columns={{ initial: '1', md: '2' }} gap="6">
        <InputTelephone
          label={phoneNumberFormField?.label ?? ''}
          autoComplete="tel"
          error={errors?.contactPhoneNumber?.message}
          hint={hintOf(phoneNumberFormField?.hint)}
          placeholder={phoneNumberFormField?.placeholder ?? ''}
          {...register('contactPhoneNumber')}
        />
        <InputEmail
          label={emailAddressFormField?.label ?? ''}
          autoComplete="email"
          error={errors?.contactEmail?.message}
          hint={hintOf(emailAddressFormField?.hint)}
          placeholder={emailAddressFormField?.placeholder ?? ''}
          {...register('contactEmail')}
        />
      </Grid>
    </>
  );
};
